import React, { FC, ReactNode, useEffect, useRef } from "react";
import {
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseButton,
  DrawerHeader,
  DrawerPanel,
  DrawerTitle,
  V3DrawerSide,
} from "./Drawer.styled";

import { V3Icon } from "./Icon";

export type { V3DrawerSide };

export interface V3DrawerProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  width?: number;
  side?: V3DrawerSide;
  /** Names the dialog for assistive tech when no visible `title` is given. */
  ariaLabel?: string;
  closeLabel?: string;
  testID?: string;
}

export const V3Drawer: FC<V3DrawerProps> = ({
  open,
  onClose,
  children,
  title,
  width = 480,
  side = "right",
  ariaLabel = "Panel",
  closeLabel = "Close",
  testID = "v3-drawer",
}) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const titleId = `${testID}-title`;

  useEffect(() => {
    if (!open) return undefined;
    triggerRef.current = document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();
    return () => {
      triggerRef.current?.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (typeof document === "undefined" || !open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  return (
    <>
      <DrawerBackdrop
        $open={open}
        onClick={onClose}
        aria-hidden={true}
        data-testid={`${testID}-backdrop`}
      />
      <DrawerPanel
        $open={open}
        $width={width}
        $side={side}
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : ariaLabel}
        data-testid={testID}
      >
        <DrawerHeader>
          {title ? <DrawerTitle id={titleId}>{title}</DrawerTitle> : <span />}
          <DrawerCloseButton
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            data-testid={`${testID}-close`}
          >
            <V3Icon name="close" size={16} />
          </DrawerCloseButton>
        </DrawerHeader>
        <DrawerBody>{children}</DrawerBody>
      </DrawerPanel>
    </>
  );
};
